import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useNavigationTracking } from '@/hooks/useNavigationTracking';

/**
 * Global navigation tracker component
 * Tracks page views automatically when routes change
 */
export const NavigationTracker = () => {
  const location = useLocation();
  const { trackNavigation } = useNavigationTracking();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const lastPathRef = useRef<string>('');

  useEffect(() => {
    const currentPathKey = `${location.pathname}|${location.search}`;

    // Skip if path hasn't changed (guards against StrictMode double-invoke)
    if (lastPathRef.current === currentPathKey) {
      return;
    }
    lastPathRef.current = currentPathKey;

    // Clear any pending timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Debounce navigation tracking to prevent excessive calls
    timeoutRef.current = setTimeout(() => {
      // Extract data from URL if available
      const searchParams = new URLSearchParams(location.search);
      const geobloc = searchParams.get('geobloc');
      const city = searchParams.get('city');
      const state = searchParams.get('state');
      const country = searchParams.get('country');
      const addressId = searchParams.get('addressId');

      // Determine page type from pathname
      let pageType = 'page_view';
      if (location.pathname.includes('/search')) {
        pageType = 'search';
      } else if (location.pathname.includes('/address')) {
        pageType = 'address_detail';
      } else if (location.pathname.includes('/map')) {
        pageType = 'map_view';
      }

      // Track navigation with available data
      trackNavigation({
        geobloc: geobloc || undefined,
        city: city || undefined,
        state: state || undefined,
        country: country || undefined,
        address_id: addressId || undefined,
        page_type: pageType,
        metadata: {
          pathname: location.pathname,
          search: location.search
        }
      });
    }, 300); // 300ms debounce

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [location.pathname, location.search, trackNavigation]);

  return null; // This component doesn't render anything
};
